import { createAsyncThunk } from "@reduxjs/toolkit"
import { del, get, post, put } from "helpers/api_helper"
import {
  GET_ALL_PLANS,
  GET_PLAN_DETAILS,
  GET_CURRENT_SUBSCRIPTION,
  GET_PLAN_HISTORY,
  CREATE_SUBSCRIPTION,
  UPGRADE_SUBSCRIPTION,
  DOWNGRADE_SUBSCRIPTION,
  CANCEL_SUBSCRIPTION,
  RENEW_SUBSCRIPTION,
  GET_ALL_INVOICES,
  GET_INVOICE_DETAILS,
  DOWNLOAD_INVOICE,
  INITIATE_CHECKOUT,
  VERIFY_PAYMENT,
} from "helpers/url_helper"

const pickId = payload =>
  payload?.id || payload?._id || payload?.subscriptionId || payload?.planId || payload?.invoiceId

const requireId = (payload, actionName) => {
  const id = pickId(payload)
  if (!id) {
    throw new Error(`${actionName} requires payload.id`)
  }
  return id
}

// ── Plans ─────────────────────────────────────────────────────────────────────

export const getAllPlans = createAsyncThunk(GET_ALL_PLANS, async () => {
  const response = await get(GET_ALL_PLANS)
  return response
})

export const getPlanDetails = createAsyncThunk(
  GET_PLAN_DETAILS,
  async payload => {
    const id = requireId(payload, "GET_PLAN_DETAILS")
    const response = await get(`plans/${id}`)
    return response
  }
)

// ── Current Subscription ──────────────────────────────────────────────────────

export const getCurrentSubscription = createAsyncThunk(
  GET_CURRENT_SUBSCRIPTION,
  async (payload = {}) => {
    const params = payload?.forBilling ? { forBilling: true } : {}
    const response = await get(GET_CURRENT_SUBSCRIPTION, { params })
    return response
  }
)

export const getPlanHistory = createAsyncThunk(GET_PLAN_HISTORY, async payload => {
  const response = await get(GET_PLAN_HISTORY, { params: payload || {} })
  return response
})

// ── Subscription Lifecycle ────────────────────────────────────────────────────

export const createSubscription = createAsyncThunk(
  CREATE_SUBSCRIPTION,
  async payload => {
    const response = await post("subscriptions/checkout", payload)
    return response
  }
)

export const upgradeSubscription = createAsyncThunk(
  UPGRADE_SUBSCRIPTION,
  async payload => {
    const id = requireId(payload, "UPGRADE_SUBSCRIPTION")
    const response = await put(`subscriptions/${id}/upgrade`, payload)
    return response
  }
)

export const downgradeSubscription = createAsyncThunk(
  DOWNGRADE_SUBSCRIPTION,
  async payload => {
    const id = requireId(payload, "DOWNGRADE_SUBSCRIPTION")
    const response = await put(`subscriptions/${id}/downgrade`, payload)
    return response
  }
)

export const cancelSubscription = createAsyncThunk(
  CANCEL_SUBSCRIPTION,
  async payload => {
    const id = requireId(payload, "CANCEL_SUBSCRIPTION")
    const { id: _id, ...body } = payload
    const response = await del(`subscriptions/${id}/cancel`, body)
    return response
  }
)

export const revokeScheduledCancellation = createAsyncThunk(
  "subscriptions/revokeScheduledCancellation",
  async payload => {
    const id = requireId(payload, "REVOKE_SCHEDULED_CANCELLATION")
    const response = await post(`subscriptions/${id}/cancel/revoke`, {})
    return response
  }
)

export const renewSubscription = createAsyncThunk(
  RENEW_SUBSCRIPTION,
  async payload => {
    const id = requireId(payload, "RENEW_SUBSCRIPTION")
    const response = await post(`subscriptions/${id}/renew`, payload)
    return response
  }
)

// ── Invoices ──────────────────────────────────────────────────────────────────

export const getAllInvoices = createAsyncThunk(
  GET_ALL_INVOICES,
  async payload => {
    const response = await get("invoices", { params: payload || {} })
    return response
  }
)

export const getInvoiceDetails = createAsyncThunk(
  GET_INVOICE_DETAILS,
  async payload => {
    const id = requireId(payload, "GET_INVOICE_DETAILS")
    const response = await get(`invoices/${id}`)
    return response
  }
)

export const downloadInvoice = createAsyncThunk(
  DOWNLOAD_INVOICE,
  async payload => {
    const id = requireId(payload, "DOWNLOAD_INVOICE")
    const response = await get(`invoices/${id}/download`)
    return response
  }
)

// ── Checkout ──────────────────────────────────────────────────────────────────

export const initiateCheckout = createAsyncThunk(
  INITIATE_CHECKOUT,
  async payload => {
    const gateway = payload.paymentGateway || "tap"
    const endpoint =
      gateway === "checkout" ? "subscriptions/checkout" : "subscriptions/tap"
    const { paymentGateway, ...body } = payload
    const response = await post(endpoint, body)
    return response
  }
)

export const verifyPayment = createAsyncThunk(
  VERIFY_PAYMENT,
  async payload => {
    const response = await post(VERIFY_PAYMENT, {
      ...payload,
      gateway: payload.gateway || "tap",
    })
    return response
  }
)
